import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { 
  PrivacyPolicy,
  CreatePrivacyPolicyRequest,
  UpdatePrivacyPolicyRequest,
  PrivacyPolicyListResponse
} from '../models/sala.model';
import { environment } from '../../environments/environment';
import { friendlyError } from '../utils/friendly-error';

@Injectable({
  providedIn: 'root'
})
export class PrivacyPolicyService {
  private readonly API_URL = `${environment.apiUrl}/privacy-policy`;
  
  // Señal reactiva para las políticas
  public readonly policies = signal<PrivacyPolicy[]>([]);

  constructor(private http: HttpClient) {}

  /**
   * Obtener todas las políticas de un negocio
   */
  getPoliciesByBusiness(businessId: number, includeInactive: boolean = false): Observable<PrivacyPolicyListResponse> {
    let params = new HttpParams();
    if (includeInactive) {
      params = params.set('include_inactive', 'true');
    }
    
    return this.http.get<PrivacyPolicyListResponse>(`${this.API_URL}/business/${businessId}`, { params }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Obtener política activa de un negocio (pública)
   */
  getActivePolicy(businessId: number): Observable<PrivacyPolicy> {
    return this.http.get<PrivacyPolicy>(`${this.API_URL}/business/${businessId}/active`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Obtener una política específica por ID
   */
  getPolicyById(id: number): Observable<PrivacyPolicy> {
    return this.http.get<PrivacyPolicy>(`${this.API_URL}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Crear nueva política
   */
  createPolicy(request: CreatePrivacyPolicyRequest): Observable<PrivacyPolicy> {
    return this.http.post<PrivacyPolicy>(this.API_URL, request).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar política
   */
  updatePolicy(id: number, request: UpdatePrivacyPolicyRequest): Observable<PrivacyPolicy> {
    return this.http.put<PrivacyPolicy>(`${this.API_URL}/${id}`, request).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Activar política
   */
  activatePolicy(id: number): Observable<PrivacyPolicy> {
    return this.http.patch<PrivacyPolicy>(`${this.API_URL}/${id}/activate`, {}).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Desactivar política
   */
  deactivatePolicy(id: number): Observable<PrivacyPolicy> {
    return this.http.patch<PrivacyPolicy>(`${this.API_URL}/${id}/deactivate`, {}).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar política
   */
  deletePolicy(id: number): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Cargar políticas en la señal reactiva
   */
  loadPolicies(businessId: number, includeInactive: boolean = true): void {
    this.getPoliciesByBusiness(businessId, includeInactive).subscribe({
      next: (response) => {
        this.policies.set(response.privacyPolicies);
      },
      error: (error) => {
        console.error('Error al cargar políticas:', error);
      }
    });
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMessage = friendlyError(error, 'Ocurrió un error. Intenta nuevamente.');
    return throwError(() => new Error(errorMessage));
  }
}

